/**
 * Type Catalog API Tools
 *
 * Defines client-side agentic tools that wrap the CefSharp-bound TypeCatalogApi.
 * Each tool maps to a method on the C# TypeCatalogApi class exposed via CefSharp.
 *
 * CefSharp specifics:
 *   - Methods are camelCase on the JS side
 *   - All methods return Promise<string> (JSON-encoded)
 *   - Arguments must be passed positionally (not as an object)
 */

import { toToolDisplayName, type RuntimeToolDef } from "./runtimeTypes";
import { getAPI } from "../services/workspaceService";
import { invokeCefSharpMethod } from "../services/cefSharpBinding";

const TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Tool specifications
// ---------------------------------------------------------------------------

interface TypeCatalogApiToolSpec {
  /** Method name on the JS-side typeCatalogApi object (camelCase). */
  method: string;
  /** Tool name exposed to the agent. */
  name: string;
  description: string;
  parameters: RuntimeToolDef["definition"]["parameters"];
  /** Ordered argument names matching the C# method signature. */
  argOrder: string[];
}

const TOOL_SPECS: TypeCatalogApiToolSpec[] = [
  // ── Feature catalogue ─────────────────────────────────────────────────
  {
    method: "getFeatureCatalog",
    name: "WS-typeCatalogApi-getFeatureCatalog",
    description:
      "Returns a JSON object with the feature catalogue metadata for the current model. " +
      "Includes persistentId, name, standard, version, owner, and producer.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Feature types ─────────────────────────────────────────────────────
  {
    method: "getFeatureTypes",
    name: "WS-typeCatalogApi-getFeatureTypes",
    description:
      "Returns a paginated JSON list of feature types in the current model. " +
      "By default only types with instanceCount > 0 are returned (usedOnly=true). " +
      "Pass usedOnly=false to include all types from the catalog. " +
      "Response includes totalCount, offset, count, and a featureTypes array. " +
      "Each entry includes featureTypeId, name, shortName, description, instanceCount, " +
      "isAbstract, isDerived, isReadOnly, and superTypeId.",
    parameters: {
      type: "object",
      properties: {
        offset: {
          type: "number",
          description: "0-based start index for pagination (default 0).",
        },
        limit: {
          type: "number",
          description:
            "Maximum number of feature types to return (default 100, max 500).",
        },
        usedOnly: {
          type: "boolean",
          description:
            "When true (default), only feature types with instanceCount > 0 are returned. Set false to include all.",
        },
      },
      required: ["offset", "limit"],
    },
    argOrder: ["offset", "limit", "usedOnly"],
  },
  {
    method: "searchFeatureTypes",
    name: "WS-typeCatalogApi-searchFeatureTypes",
    description:
      "Searches feature types by name (case-insensitive substring match). " +
      "By default only types with instanceCount > 0 are returned (usedOnly=true). " +
      "Pass usedOnly=false to include all types from the catalog. " +
      "Returns a paginated JSON result with totalCount, offset, count, and featureTypes array.",
    parameters: {
      type: "object",
      properties: {
        nameFilter: {
          type: "string",
          description: "The search text to match against feature type names.",
        },
        offset: {
          type: "number",
          description: "0-based start index for pagination (default 0).",
        },
        limit: {
          type: "number",
          description:
            "Maximum number of feature types to return (default 100, max 500).",
        },
        usedOnly: {
          type: "boolean",
          description:
            "When true (default), only feature types with instanceCount > 0 are returned. Set false to include all.",
        },
      },
      required: ["nameFilter", "offset", "limit"],
    },
    argOrder: ["nameFilter", "offset", "limit", "usedOnly"],
  },
  {
    method: "getFeatureType",
    name: "WS-typeCatalogApi-getFeatureType",
    description:
      "Returns full detail for a single feature type identified by its numeric ID. " +
      "Includes attributeTypes array (with id, name, valueType, isMandatory, isEnum, legalValues, etc.) " +
      "and spatialAttributeTypes array (with id, name, spatialType, isMandatory).",
    parameters: {
      type: "object",
      properties: {
        featureTypeId: {
          type: "number",
          description: "The numeric feature type ID.",
        },
      },
      required: ["featureTypeId"],
    },
    argOrder: ["featureTypeId"],
  },

  // ── Feature type groups ───────────────────────────────────────────────
  {
    method: "getFeatureTypeGroups",
    name: "WS-typeCatalogApi-getFeatureTypeGroups",
    description:
      "Returns a JSON array of feature type groups in the current model. " +
      "Each group includes id, name, shortName, description, and a featureTypeIds array.",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },

  // ── Attribute types ───────────────────────────────────────────────────
  {
    method: "getAttributeTypes",
    name: "WS-typeCatalogApi-getAttributeTypes",
    description:
      "Returns a JSON array of all attribute types defined in the current model. " +
      "Each entry includes id, name, shortName, description, valueType, isMandatory, " +
      "isReadOnly, isEnum, unitName, decimals, and legalValues (if enum).",
    parameters: { type: "object", properties: {}, required: [] },
    argOrder: [],
  },
];

// ---------------------------------------------------------------------------
// CefSharp-aware callback factory
// ---------------------------------------------------------------------------

function createCefSharpCallback(
  method: string,
  argOrder: string[],
): (args: Record<string, unknown>) => Promise<string> {
  return async (args: Record<string, unknown>): Promise<string> => {
    try {
      const api = getAPI();
      const typeCatalogApi = api?.typeCatalogApi;
      if (!typeCatalogApi) {
        return JSON.stringify({
          success: false,
          error:
            "TypeCatalogApi not bound — CefSharp.BindObjectAsync may not have completed.",
        });
      }

      const fn = typeCatalogApi[method];
      if (typeof fn !== "function") {
        return JSON.stringify({
          success: false,
          error: `Unknown method: typeCatalogApi.${method}`,
        });
      }

      const positionalArgs = argOrder.map((name) => args?.[name]);
      const raw = await invokeCefSharpMethod<string>(
        "typeCatalogApi",
        typeCatalogApi,
        method,
        positionalArgs,
      );

      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        data = raw;
      }

      return JSON.stringify({ success: true, data });
    } catch (error) {
      return JSON.stringify({
        success: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create RuntimeToolDef entries for every TypeCatalogApi method.
 * Call this after `CefSharp.BindObjectAsync("typeCatalogApi")` has resolved.
 */
export function createTypeCatalogApiTools(): Record<string, RuntimeToolDef> {
  const tools: Record<string, RuntimeToolDef> = {};

  for (const spec of TOOL_SPECS) {
    tools[spec.name] = {
      definition: {
        name: spec.name,
        description: spec.description,
        deferred: true,
        title: toToolDisplayName(spec.name),
        parameters: spec.parameters,
      },
      callback: createCefSharpCallback(spec.method, spec.argOrder),
      timeOutInMs: TIMEOUT_MS,
    };
  }

  return tools;
}
